(() => {
  /* ------------------------------------------------------------------
   * Ziehzeit-Timer: Minuten aus „3-4 Minuten“ / „5 Min.“ / „2,5 Min“ lesen.
   * Ton nur, wenn der Nutzer den Timer selbst gestartet hat.
   * ------------------------------------------------------------------ */
  const CIRC = 2 * Math.PI * 44;

  if (!customElements.get('e2-timer')) {
    customElements.define(
      'e2-timer',
      class E2Timer extends HTMLElement {
        connectedCallback() {
          this.minutes = E2Timer.parseMinutes(this.dataset.time || '');
          this.ring = this.querySelector('[data-e2-timer-ring]');
          this.display = this.querySelector('[data-e2-timer-display]');
          this.startBtn = this.querySelector('[data-e2-timer-start]');
          this.stopBtn = this.querySelector('[data-e2-timer-stop]');
          this.done = this.querySelector('[data-e2-timer-done]');
          this.userStarted = false;
          this.raf = null;

          if (!this.minutes || !this.startBtn) {
            this.hidden = true;
            return;
          }
          this.total = Math.round(this.minutes * 60);
          if (this.ring) {
            this.ring.style.strokeDasharray = String(CIRC);
            this.ring.style.strokeDashoffset = '0';
          }
          this.setDisplay(this.total);
          this.startBtn.addEventListener('click', () => this.start());
          if (this.stopBtn) this.stopBtn.addEventListener('click', () => this.stop());
        }

        disconnectedCallback() {
          this.stop();
        }

        static parseMinutes(text) {
          const m = String(text).replace(',', '.').match(/(\d+(?:\.\d+)?)/);
          if (!m) return 0;
          const value = parseFloat(m[1]);
          if (!isFinite(value) || value <= 0) return 0;
          // Sekundenangaben („30 Sek.“) in Minuten umrechnen
          if (/sek|sec/i.test(text) && !/min/i.test(text)) return value / 60;
          return value;
        }

        setDisplay(seconds) {
          if (!this.display) return;
          const s = Math.max(0, Math.round(seconds));
          const mm = Math.floor(s / 60);
          const ss = s % 60;
          this.display.textContent = `${mm}:${ss < 10 ? '0' : ''}${ss}`;
        }

        start() {
          this.stop();
          this.userStarted = true;
          this.classList.add('is-running');
          this.classList.remove('is-done');
          if (this.done) this.done.hidden = true;
          this.startBtn.hidden = true;
          if (this.stopBtn) this.stopBtn.hidden = false;
          this.startedAt = performance.now();
          const tick = (now) => {
            const elapsed = (now - this.startedAt) / 1000;
            const remaining = this.total - elapsed;
            if (this.ring) {
              const progress = Math.min(1, elapsed / this.total);
              this.ring.style.strokeDashoffset = String(CIRC * progress);
            }
            this.setDisplay(Math.ceil(remaining));
            if (remaining <= 0) {
              this.finish();
              return;
            }
            this.raf = requestAnimationFrame(tick);
          };
          this.raf = requestAnimationFrame(tick);
        }

        stop() {
          if (this.raf) cancelAnimationFrame(this.raf);
          this.raf = null;
          this.classList.remove('is-running');
          if (this.startBtn) this.startBtn.hidden = false;
          if (this.stopBtn) this.stopBtn.hidden = true;
          if (this.ring) this.ring.style.strokeDashoffset = '0';
          if (this.total) this.setDisplay(this.total);
        }

        finish() {
          this.raf = null;
          this.classList.remove('is-running');
          this.classList.add('is-done');
          this.setDisplay(0);
          if (this.ring) this.ring.style.strokeDashoffset = String(CIRC);
          if (this.startBtn) {
            this.startBtn.hidden = false;
            this.startBtn.textContent = 'Noch einmal';
          }
          if (this.stopBtn) this.stopBtn.hidden = true;
          if (this.done) this.done.hidden = false;
          if (this.userStarted) this.beep();
        }

        beep() {
          try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            const ctx = new Ctx();
            const play = (at, freq) => {
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.type = 'sine';
              osc.frequency.value = freq;
              gain.gain.setValueAtTime(0.0001, at);
              gain.gain.exponentialRampToValueAtTime(0.12, at + 0.02);
              gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.28);
              osc.connect(gain).connect(ctx.destination);
              osc.start(at);
              osc.stop(at + 0.3);
            };
            const t = ctx.currentTime + 0.05;
            play(t, 880);
            play(t + 0.4, 1175);
            setTimeout(() => ctx.close && ctx.close(), 1500);
          } catch (e) {
            /* kein Ton möglich – Text reicht */
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------
   * Produktseite: Varianten-Pillen, Preis/SKU/Bild, Menge, Sticky-Leiste.
   * ------------------------------------------------------------------ */
  if (!customElements.get('e2-product')) {
    customElements.define(
      'e2-product',
      class E2Product extends HTMLElement {
        connectedCallback() {
          const read = (selector, fallback) => {
            const el = this.querySelector(selector);
            try {
              return el ? JSON.parse(el.textContent) : fallback;
            } catch (e) {
              return fallback;
            }
          };
          this.variants = read('[data-e2-variants]', []);
          this.strings = read('[data-e2-strings]', { add: 'In den Warenkorb', soldOut: 'Ausverkauft', unavailable: 'Nicht verfügbar' });

          this.form = this.querySelector('form[data-type="add-to-cart-form"]');
          this.idInput = this.querySelector('[data-e2-variant-id]');
          this.submit = this.querySelector('[data-e2-submit]');
          this.submitText = this.querySelector('[data-e2-submit-text]');
          this.priceEls = this.querySelectorAll('[data-e2-price]');
          this.compareEl = this.querySelector('[data-e2-compare]');
          this.unitEl = this.querySelector('[data-e2-unit]');
          this.skuWrap = this.querySelector('[data-e2-sku]');
          this.skuValue = this.querySelector('[data-e2-sku-value]');
          this.optionSets = Array.from(this.querySelectorAll('[data-e2-option]'));

          this.optionSets.forEach((set) => {
            set.querySelectorAll('input[type="radio"]').forEach((input) => {
              input.addEventListener('change', () => this.onVariantChange());
            });
          });

          this.initQuantity();
          this.initGallery();
          this.initSticky();
          this.initBack();
          this.markAvailability();
        }

        /* --- Varianten --- */
        selectedOptions() {
          return this.optionSets.map((set) => {
            const checked = set.querySelector('input[type="radio"]:checked');
            return checked ? checked.value : null;
          });
        }

        findVariant(options) {
          return this.variants.find((variant) =>
            options.every((value, index) => value === null || variant.options[index] === value)
          );
        }

        onVariantChange() {
          const options = this.selectedOptions();
          const variant = this.findVariant(options);
          this.markAvailability();
          if (!variant) {
            this.setSubmit(false, this.strings.unavailable);
            return;
          }
          if (this.idInput) this.idInput.value = variant.id;
          this.priceEls.forEach((el) => (el.textContent = variant.price));
          if (this.compareEl) {
            this.compareEl.hidden = !variant.compare;
            this.compareEl.textContent = variant.compare || '';
          }
          if (this.unitEl) {
            this.unitEl.hidden = !variant.unit;
            this.unitEl.textContent = variant.unit || '';
          }
          if (this.skuWrap) {
            this.skuWrap.hidden = !variant.sku;
            if (this.skuValue) this.skuValue.textContent = variant.sku || '';
          }
          this.setSubmit(variant.available, variant.available ? this.strings.add : this.strings.soldOut);
          if (variant.media) this.showSlide(variant.media);
          try {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', url.toString());
          } catch (e) {
            /* ignorieren */
          }
        }

        setSubmit(enabled, text) {
          if (this.submit) {
            this.submit.disabled = !enabled;
            if (enabled) this.submit.removeAttribute('aria-disabled');
          }
          if (this.submitText && text) this.submitText.textContent = text;
          if (this.stickyBtn) {
            this.stickyBtn.disabled = !enabled;
            if (text) this.stickyBtn.textContent = text;
          }
        }

        /* Pillen ausgrauen, deren Kombination mit den übrigen Auswahlen nicht lieferbar ist. */
        markAvailability() {
          const current = this.selectedOptions();
          this.optionSets.forEach((set, index) => {
            set.querySelectorAll('input[type="radio"]').forEach((input) => {
              const probe = current.slice();
              probe[index] = input.value;
              const exists = this.variants.some(
                (variant) =>
                  probe.every((value, i) => value === null || variant.options[i] === value) && variant.available
              );
              const pill = input.closest('.e2-pill');
              if (pill) pill.classList.toggle('e2-pill--unavailable', !exists);
            });
          });
        }

        /* --- Menge --- */
        initQuantity() {
          const qty = this.querySelector('[data-e2-qty]');
          if (!qty) return;
          const input = qty.querySelector('input');
          const change = (delta) => {
            const value = parseInt(input.value, 10);
            const next = Math.max(1, (isNaN(value) ? 1 : value) + delta);
            input.value = next;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          };
          const minus = qty.querySelector('[data-e2-qty-minus]');
          const plus = qty.querySelector('[data-e2-qty-plus]');
          if (minus) minus.addEventListener('click', () => change(-1));
          if (plus) plus.addEventListener('click', () => change(1));
          input.addEventListener('change', () => {
            const value = parseInt(input.value, 10);
            if (isNaN(value) || value < 1) input.value = 1;
          });
        }

        /* --- Galerie --- */
        initGallery() {
          this.slides = Array.from(this.querySelectorAll('[data-e2-slide]'));
          this.thumbs = Array.from(this.querySelectorAll('[data-e2-thumb]'));
          this.thumbs.forEach((thumb) => {
            thumb.addEventListener('click', () => this.showSlide(Number(thumb.dataset.e2Thumb)));
          });
        }

        showSlide(position) {
          if (!this.slides.length) return;
          const target = this.slides.find((slide) => Number(slide.dataset.e2Slide) === position);
          if (!target) return;
          this.slides.forEach((slide) => slide.classList.toggle('is-active', slide === target));
          this.thumbs.forEach((thumb) => {
            const active = Number(thumb.dataset.e2Thumb) === position;
            thumb.classList.toggle('is-active', active);
            if (active) thumb.setAttribute('aria-current', 'true');
            else thumb.removeAttribute('aria-current');
          });
        }

        /* --- Sticky-Leiste (mobil): sichtbar, sobald der echte Button aus dem Bild ist --- */
        initSticky() {
          this.sticky = this.querySelector('[data-e2-sticky]');
          this.stickyBtn = this.querySelector('[data-e2-sticky-btn]');
          if (!this.sticky || !this.stickyBtn || !this.form) return;
          this.stickyBtn.addEventListener('click', () => {
            if (this.submit && this.submit.disabled) return;
            if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
            else this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          });
          if (!('IntersectionObserver' in window) || !this.submit) {
            this.sticky.classList.add('is-visible');
            this.sticky.setAttribute('aria-hidden', 'false');
            return;
          }
          const io = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
                this.sticky.classList.toggle('is-visible', show);
                this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
              });
            },
            { threshold: 0 }
          );
          io.observe(this.submit);
        }

        /* --- Zurück-Link: History, wenn man aus dem Shop kommt --- */
        initBack() {
          const back = this.querySelector('[data-e2-back]');
          if (!back) return;
          back.addEventListener('click', (event) => {
            if (document.referrer && document.referrer.indexOf(window.location.host) !== -1 && window.history.length > 1) {
              event.preventDefault();
              window.history.back();
            }
          });
        }
      }
    );
  }
})();
